'use client';

import { c as _c } from "react/compiler-runtime";
import type { InboxLatestCubicReview, InboxRequestedTeam } from '@/app/(home)/inbox/types';
import type { CubicReviewerPresentation } from '@/app/(home)/inbox/utils/cubic-reviewer-presentation';
import type { InboxAutoFixLoop } from '@/components/inbox/pr-inbox-cubic-status';
import type { ReviewerAvatar, ReviewState } from '@/components/reviewers/reviewer-avatar-stack';
import type { ReviewRun } from '@/data/reviews/use-review-statuses';
import type { AutoFixLoopStatusView } from '@/lib/auto-fix-loop/status';
import type { CodeSessionPullRequest } from '@/lib/code-sessions/pull-request';
import type { PrV2ChecksDisplay, PrV2CheckSummary } from '@/lib/pr-v2/check-run-presentation';
import type { CanonicalReviewer, PrV2ReviewDecision } from '@/lib/pr-v2/reviewers';
import type { ReviewerStateValues } from '@/types/github';
import type { PrRef } from './session-panel/pr-ref';
import { getCubicReviewerPresentation } from '@/app/(home)/inbox/utils/cubic-reviewer-presentation';
import { compareReviewRunsByRecency, deriveCubicRunStatus, isCubicReviewer } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/sidebar/pr-v2-sidebar-model';
import { REVIEW_STATES } from '@/components/reviewers/reviewer-avatar-stack';
import { isTimedOutPendingRun } from '@/data/reviews/review-run-optimistic';
import { useReviewRunsQuery } from '@/data/reviews/use-review-statuses';
import { isPrV2AccessError } from '@/data/v2/pr/fetch-pr-v2';
import { usePrV2Checks } from '@/data/v2/pr/use-pr-v2-checks';
import { usePrV2CubicReviewSummary } from '@/data/v2/pr/use-pr-v2-cubic-review-summary';
import { usePrV2Reviews } from '@/data/v2/pr/use-pr-v2-reviews';
import { usePrV2Summary } from '@/data/v2/pr/use-pr-v2-summary';
import { describeAutoFixLoop, resolveAutoFixLoopViewActivity } from '@/lib/auto-fix-loop/presentation';
import { codeSessionPullRequestState } from '@/lib/code-sessions/pull-request-state';
import { useMergeabilityStatus } from '@/lib/hooks/mergeability-status/use-mergeability-status';
import { buildPrV2ChecksDisplay, summarizePrV2ChecksDisplay } from '@/lib/pr-v2/check-run-presentation';
import { buildPrV2Reviewers, getPrV2ReviewDecision } from '@/lib/pr-v2/reviewers';
import { ReviewerState } from '@/types/github';
import { isAutomationLogin } from '@/utils/github/bot-detection';

/** Where the session's pull request stands, in the parts of an inbox row. */
export type SessionPrCardStatus = {
  /** The PR v2 summary's title, state and conflicts, and who opened it when, or null until it loads. */
  pullRequest: (Pick<CodeSessionPullRequest, 'state' | 'hasConflicts'> & {
    title: string | null;
    author: {
      login: string | null;
      avatarUrl: string | null;
    };
    /** When it was opened, as the summary's ISO timestamp. */
    openedAt: string | null;
  }) | null;
  /**
   * Why there is no pull request to show: the viewer may not read it, or it failed to load. null
   * while it loads and once it has, even when a later refetch fails.
   */
  loadFailure: 'no-access' | 'failed' | null;
  /** null until the PR v2 summary loads. */
  diff: {
    additions: number;
    deletions: number;
    changedFiles: number;
  } | null;
  /** Inbox-shaped counts; null while loading or when the head has no runs. */
  checks: PrV2CheckSummary | null;
  /** cubic threads still open on the pull request; undefined until cubic's review summary loads. */
  openIssueCount: number | undefined;
  /** The inbox slot's taxonomy. */
  cubicReview: CubicReviewerPresentation;
};

/** The people reviewing the session's pull request, and the teams asked to; cubic has its own slot. */
export type SessionPrReviewers = {
  reviewers: ReviewerAvatar[];
  teams: InboxRequestedTeam[];
  /** Where every recorded review, cubic's and bots' included, leaves it; undefined until they load. */
  reviewDecision: PrV2ReviewDecision | null | undefined;
};

/**
 * The PR tab's check counts (cubic's own runs dropped, required checks that have not reported
 * still outstanding) in the inbox's shape, whose marks have no skipped or expected state.
 */
export function toSessionPrCheckSummary(display: Pick<PrV2ChecksDisplay, 'counts' | 'pendingRequiredChecks'>): PrV2CheckSummary | null {
  const summary = summarizePrV2ChecksDisplay(display);
  return summary.total === 0 ? null : summary;
}

/**
 * cubic's review as the inbox projects it. It is running while a run is in progress, and otherwise
 * the latest run's outcome, so a failed review that a rerun completed reads as completed. The PR
 * sidebar lets any failed run win instead.
 */
export function toSessionPrCubicReview(reviewRuns: ReviewRun[], unresolvedCommentCount: number | undefined): InboxLatestCubicReview {
  const running = deriveCubicRunStatus(reviewRuns).status === 'running';
  // A placeholder for a review that never started stops counting as running once it times
  // out, so it cannot be the latest run either.
  const latest = reviewRuns.filter(run => !isTimedOutPendingRun(run)).sort(compareReviewRunsByRecency)[0];
  return {
    status: running ? 'running' : latest?.status ?? 'idle',
    updatedAt: latest?.updatedAt ?? null,
    unresolvedCommentCount
  };
}

/**
 * cubic's review as the inbox slot presents it. A finished review stays hidden until its open
 * issues load, since reading the unknown count as none would show it clean, then with a count.
 */
export function toSessionPrCubicPresentation(reviewRuns: ReviewRun[], openIssueCount: number | undefined): CubicReviewerPresentation {
  const review = getCubicReviewerPresentation(toSessionPrCubicReview(reviewRuns, openIssueCount));
  return review.kind === 'clean' && openIssueCount === undefined ? {
    kind: 'hidden'
  } : review;
}

/**
 * The auto-fix loop as the inbox row reads it, or null while its status loads or the loop is off,
 * since the inbox carries only loops that are on. The card's switch keeps its own flip, so none
 * reads as pending here.
 */
export function toSessionPrAutoFixLoop(data: AutoFixLoopStatusView | undefined, cubicReview: CubicReviewerPresentation, openIssueCount: number | undefined): InboxAutoFixLoop | null {
  if (!data?.enabled) {
    return null;
  }
  const activity = resolveAutoFixLoopViewActivity({
    data,
    pendingTarget: null,
    cubicReviewRunning: cubicReview.kind === 'running',
    openIssueCount
  });
  return {
    activity,
    ...describeAutoFixLoop(activity)
  };
}
const REVIEWER_AVATAR_STATUS: Record<ReviewerStateValues, ReviewState> = {
  APPROVED: REVIEW_STATES.APPROVED,
  CHANGES_REQUESTED: REVIEW_STATES.CHANGES_REQUESTED,
  COMMENTED: REVIEW_STATES.COMMENTED,
  DISMISSED: REVIEW_STATES.DISMISSED,
  REQUESTED_REVIEW: REVIEW_STATES.WAITING
};

/**
 * The PR page's reviewers, as the inbox's avatar stack draws them. Like the inbox, it keeps
 * people only. cubic has its own slot, and other bots, such as Copilot's reviewer, are left out.
 */
export function toSessionPrReviewers(reviewers: CanonicalReviewer[]): ReviewerAvatar[] {
  return reviewers.filter(reviewer => !isCubicReviewer(reviewer.login) && !isAutomationLogin(reviewer.login)).map(reviewer => ({
    login: reviewer.login,
    avatarUrl: reviewer.avatarUrl,
    status: REVIEWER_AVATAR_STATUS[reviewer.state],
    isCurrentlyRequested: reviewer.state === ReviewerState.REQUESTED_REVIEW
  }));
}

/**
 * The session pull request card's status, from the PR v2 queries the PR tab reads, so View PR
 * opens on a warm cache and both surfaces count the same things.
 */
export function useSessionPrCardStatus(t0, t1) {
  const $ = _c(42);
  const {
    owner,
    repo,
    number: prNumber
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    deferCubicReview: t3
  } = t2;
  const deferCubicReview = t3 === undefined ? true : t3;
  const number = String(prNumber);
  const summaryQuery = usePrV2Summary(owner, repo, number);
  const pullRequest = summaryQuery.data?.pullRequest;
  const loaded = pullRequest !== undefined;
  const t4 = pullRequest?.headSha ?? null;
  let t5;
  if ($[2] !== loaded || $[3] !== t4) {
    t5 = {
      enabled: loaded,
      currentHeadSha: t4
    };
    $[2] = loaded;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const checksQuery = usePrV2Checks(owner, repo, number, t5);
  const t6 = summaryQuery.data?.mergeabilityStatus ?? null;
  let t7;
  if ($[5] !== loaded || $[6] !== t6) {
    t7 = {
      initialData: t6,
      enabled: loaded
    };
    $[5] = loaded;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const {
    status: mergeabilityStatus
  } = useMergeabilityStatus(owner, repo, number, t7);
  let t8;
  if ($[8] !== deferCubicReview || $[9] !== loaded) {
    t8 = {
      enabled: loaded,
      deferred: deferCubicReview
    };
    $[8] = deferCubicReview;
    $[9] = loaded;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const cubicReviewSummaryQuery = usePrV2CubicReviewSummary(owner, repo, number, t8);
  const {
    reviewRuns
  } = useReviewRunsQuery(owner, repo, number);
  const openIssueCount = cubicReviewSummaryQuery.data?.outstandingComments;
  let t9;
  if ($[11] !== openIssueCount || $[12] !== reviewRuns) {
    t9 = toSessionPrCubicPresentation(reviewRuns, openIssueCount);
    $[11] = openIssueCount;
    $[12] = reviewRuns;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const cubicReview = t9;
  if (!pullRequest) {
    let t10;
    if ($[14] !== summaryQuery) {
      t10 = summaryLoadFailure(summaryQuery);
      $[14] = summaryQuery;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    let t11;
    if ($[16] !== cubicReview || $[17] !== openIssueCount || $[18] !== t10) {
      t11 = {
        pullRequest: null,
        loadFailure: t10,
        diff: null,
        checks: null,
        openIssueCount,
        cubicReview
      };
      $[16] = cubicReview;
      $[17] = openIssueCount;
      $[18] = t10;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    return t11;
  }
  const metadata = pullRequest.metadataSummary;
  let t10;
  if ($[20] !== pullRequest) {
    t10 = codeSessionPullRequestState(pullRequest);
    $[20] = pullRequest;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const state = t10;
  const t11 = state === "open" || state === "draft" ? mergeabilityStatus?.mergeConflicts.hasConflicts : false;
  let t12;
  if ($[22] !== metadata.createdAt || $[23] !== pullRequest.author || $[24] !== pullRequest.title || $[25] !== state || $[26] !== t11) {
    t12 = {
      title: pullRequest.title,
      author: pullRequest.author,
      openedAt: metadata.createdAt,
      state,
      hasConflicts: t11
    };
    $[22] = metadata.createdAt;
    $[23] = pullRequest.author;
    $[24] = pullRequest.title;
    $[25] = state;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = metadata.additions ?? 0;
  const t14 = metadata.deletions ?? 0;
  const t15 = metadata.changedFiles ?? 0;
  let t16;
  if ($[28] !== t13 || $[29] !== t14 || $[30] !== t15) {
    t16 = {
      additions: t13,
      deletions: t14,
      changedFiles: t15
    };
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== checksQuery.data || $[33] !== mergeabilityStatus?.checksStatus?.requiredChecks || $[34] !== pullRequest.headSha) {
    t17 = checksQuery.data ? toSessionPrCheckSummary(buildPrV2ChecksDisplay({
      checks: checksQuery.data,
      headSha: pullRequest.headSha,
      requiredChecks: mergeabilityStatus?.checksStatus?.requiredChecks
    })) : null;
    $[32] = checksQuery.data;
    $[33] = mergeabilityStatus?.checksStatus?.requiredChecks;
    $[34] = pullRequest.headSha;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== cubicReview || $[37] !== openIssueCount || $[38] !== t12 || $[39] !== t16 || $[40] !== t17) {
    t18 = {
      pullRequest: t12,
      diff: t16,
      checks: t17,
      loadFailure: null,
      openIssueCount,
      cubicReview
    };
    $[36] = cubicReview;
    $[37] = openIssueCount;
    $[38] = t12;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
}
function summaryLoadFailure(query: {
  isError: boolean;
  error: unknown;
}): SessionPrCardStatus['loadFailure'] {
  if (!query.isError) {
    return null;
  }
  return isPrV2AccessError(query.error) ? 'no-access' : 'failed';
}

/**
 * The PR page's reviewers of the session's pull request, as the session card's avatar stack draws
 * them. Only that card shows them, so the status leaves them out and the hover card, which reads
 * the status, asks GitHub for no reviews.
 */
export function useSessionPrReviewers(t0) {
  const $ = _c(14);
  const {
    owner,
    repo,
    number: prNumber
  } = t0;
  const number = String(prNumber);
  const summaryQuery = usePrV2Summary(owner, repo, number);
  const metadata = summaryQuery.data?.pullRequest.metadataSummary;
  const t1 = metadata !== undefined;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const reviewsQuery = usePrV2Reviews(owner, repo, number, t2);
  if (!metadata) {
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        reviewers: [],
        teams: [],
        reviewDecision: undefined
      };
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  let t3;
  if ($[3] !== metadata.reviewers || $[4] !== reviewsQuery.data) {
    t3 = toSessionPrReviewers(buildPrV2Reviewers(metadata.reviewers, reviewsQuery.data ?? []));
    $[3] = metadata.reviewers;
    $[4] = reviewsQuery.data;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== metadata.requestedTeams) {
    t4 = metadata.requestedTeams.map(_temp);
    $[6] = metadata.requestedTeams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== reviewsQuery.data) {
    t5 = reviewsQuery.data ? getPrV2ReviewDecision(reviewsQuery.data) : undefined;
    $[8] = reviewsQuery.data;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = {
      reviewers: t3,
      teams: t4,
      reviewDecision: t5
    };
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp(team) {
  return {
    ...team,
    avatar_url: null
  };
}